import { ReactElement, useState } from 'react'
import { DinamicModal } from 'components'
import { createContext } from 'use-context-selector'
import {
  hasConnectionLostError,
  verifyApiHealth
} from 'services/Instances/utils'

export const ModalContext = createContext({})

export type IModalTypes = 'success' | 'alert' | 'error' | 'info'

export interface IModalProps {
  visible?: boolean
  showLoader?: boolean
  message?: string | ReactElement
  type?: IModalTypes
  hideButtonConfirm?: boolean
  hideButtonCancel?: boolean
  hideThirdButton?: boolean
  handleThird?: () => void
  handleCancel?: () => void
  handleConfirm?: () => void
  txtButtonThird?: string
  txtButtonConfirm?: string
  txtButtonCancel?: string
  titleModal?: string | undefined
  typeButtonThird?: 'text' | 'outlined' | 'default'
  typeButtonCancel?: 'text' | 'outlined' | 'default'
  typeButtonConfirm?: 'text' | 'outlined' | 'default'
  showModal?: (_payload: IModalProps) => void
  closeModal?: () => void
  showConnectionLostModal?: () => Promise<void>
}

export const ModalProvider = ({ children }) => {
  const [modalState, setModalState] = useState<IModalProps>({
    message: '',
    visible: false
  })

  const showModal = (payload: IModalProps): void => {
    const unauthorizedMessage = window.localStorage.getItem(
      'csn-refresh-token-unauthorized-message'
    )
    const expiredSession = !!unauthorizedMessage

    if (hasConnectionLostError()) {
      showConnectionLostModal().then(() => {
        window.localStorage.removeItem('csn-api-connection-lost')
      })
      return
    }
    if (!expiredSession) return setModalState({ ...payload, visible: true })

    setModalState({
      message: unauthorizedMessage,
      type: 'alert',
      txtButtonConfirm: 'OK',
      hideButtonCancel: true,
      handleConfirm: () => {
        window.localStorage.clear()
        window.location.reload()
      },
      visible: true
    })
  }

  const showConnectionLostModal = (): Promise<void> => {
    return new Promise<void>((resolve, reject) => {
      if (window.localStorage.getItem('connErrorIsVisible')) return
      closeModal()
      setModalState({
        titleModal: 'Conexão perdida',
        type: 'info',
        message:
          'Verifique sua conexão de rede. Aguarde enquanto tentamos reconectar...',
        visible: true,
        hideButtonCancel: true,
        txtButtonConfirm: 'Fechar',
        handleConfirm: () => {
          window.localStorage.removeItem('connErrorIsVisible')
          window.localStorage.removeItem('csn-api-connection-lost')
        }
      })
      window.localStorage.setItem('connErrorIsVisible', 'true')
      verifyApiHealth()
        .then(() => {
          setModalState({
            titleModal: 'OK',
            type: 'success',
            message: 'Conexão reestabelecida!',
            visible: true,
            hideButtonCancel: true,
            txtButtonConfirm: 'Fechar'
          })
          window.localStorage.removeItem('csn-api-connection-lost')
          window.localStorage.removeItem('connErrorIsVisible')
          return resolve()
        })
        .catch(() => {
          closeModal()
          setModalState({
            message: 'Não foi possível reestabelecer a conexão.',
            type: 'error',
            txtButtonConfirm: 'Fechar',
            hideButtonCancel: true,
            visible: true
          })
        })
    })
  }

  const closeModal = (): void => setModalState({ message: '', visible: false })

  return (
    <ModalContext.Provider
      value={{ ...modalState, closeModal, showModal, showConnectionLostModal }}
    >
      {!!modalState.visible && <DinamicModal />}
      {children}
    </ModalContext.Provider>
  )
}
